import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { message } from 'antd';
import { AmplifierData, AmplifierControlData } from './amplifier';
import { amplifierMqttService } from './AmplifierMqttService';
import { useAmplifierI18n } from '../../i18n/amplifier/useAmplifierI18n';
import { AMP_VIS, ampNodeFill, ampNodeStroke } from './amplifierVisualTokens';
import {
    AntennaLabelKey,
    ANTENNA_LABEL_TO_INDEX,
    createDefaultAntennaConfig,
    hzToInput,
    patchAntennaConfigAtIndex,
    resolveAntennaDisplayName,
    validateAntennaFreqRangeHz,
} from './antennaConfig';
import { AntennaNameEditModal } from './AntennaNameEditModal';

function ampNodeRectClass(options: {
    active?: boolean;
    power?: boolean;
    tune?: boolean;
    tuning?: boolean;
}): string {
    const parts = ['amp-node-rect'];
    if (options.active) parts.push('amp-node-rect--active');
    if (options.power) parts.push('amp-node-rect--power');
    if (options.tune) parts.push('amp-node-rect--tune');
    if (options.tuning) parts.push('amp-node-rect--tuning');
    return parts.join(' ');
}
interface TopBarBottomProps {
    deviceId?: string;
    amplifierData?: AmplifierData | null;
    antennaMode?: 'manual' | 'auto';
}

/**
 * ---------- 天线部分逻辑说明 ----------
 *
 * 一、数据来源与同步
 * - inputAntenna、outputAntenna 以 amplifierData 为准（设备状态），由父组件通过 props 传入。
 * - amplifierData 变化时，useEffect 会把 inputAntenna、outputAntenna 同步到本地 amplifierControlData，
 *   并用于 handleParameterChange 下发指令。
 *
 * 二、输入天线（inputAntenna）
 * - 取值：1 = 输入天线1，2 = 输入天线2。（0 表示未选，路径不绘制）
 * - UI：左侧两个按钮「输入天线1」「输入天线2」，点击即下发；与触发模式双向绑定：天线1↔PTT1(0)，天线2↔PP2(1)。
 * - 路径：接收模式(ptt_status=0) 时路径终点由 inputAntenna 决定（1→天线1 端口，2→天线2 端口）；
 *        发射模式(ptt_status=1) 时路径起点由 inputAntenna 决定（1→从天线1 经衰减器到主管道，2→从天线2 到主管道）。
 *
 * 三、输出天线（outputAntenna）
 * - 取值：1/2/3/4 = 输出天线1～4。
 * - UI：右侧四个按钮「输出天线1」～「输出天线4」，是否可点由 antennaMode 决定。
 * - antennaMode：由父组件传入。'manual' = 可点击切换输出天线；'auto' = 不可点击（cursor: not-allowed，opacity 0.6）。
 * - 路径：接收模式(ptt_status=0) 时路径起点由 outputAntenna 决定（1～4 对应右侧四个天线端口）；
 *        发射模式(ptt_status=1) 时路径终点由 outputAntenna 决定。
 *
 * 四、信号路径与天线
 * - ptt_status=0（接收）：generateReceivePath，路径 输出天线 → 主管道 → 输入天线，需 outputAntenna 与 inputAntenna 均有值才绘制。
 * - ptt_status=1（发射）：generateTransmitPath，路径 输入天线 → 主管道 → 输出天线，同样需两者均有值才绘制。
 * - generateSignalPath 根据 amplifierData.ptt（来自 remote_control.ptt_status）在二者间切换，路径用于 SVG 虚线动画。
 *
 * ---------- 天调按钮与功放按钮逻辑说明 ----------
 *
 * 五、功放按钮（power）
 * - 取值：0 = 关闭，1 = 开启。
 * - 数据：来自 amplifierData.power，同步到 amplifierControlData；点击后通过 handleParameterChange({ power }) 下发。
 * - UI：点击在 0/1 间切换；power===1 时按钮为橙色(#cc6600)，否则灰色(#333333)。
 * - 路径影响：
 *   - 接收路径：主管道3→2 段，power===1 走功放节点(ampRight6…1)，否则走衰减器(attenuator7→6)。
 *   - 发射路径：输入天线1→主管道1 段，power===1 穿过衰减器(attenuator1…5)，power===0 绕过衰减器(直连 mainPipe1)；
 *   - 发射路径：主管道2→3 段，power===1 走功放节点(ampRight1…6)，否则走衰减器(attenuator6→7)。
 * - 联动：输入衰减器按 attenuator_list[attenuator_sel] 显示 dB；点击索引 +1 循环（含 0 档 0 dB）。
 *
 * 六、天调按钮（tuneMode）
 * - 取值：0 = 关闭，1 = 开启，2 = 调谐中（设备可能仍上报 3，UI 不再展示调谐失败）。
 * - 短按：0 ↔ 1 切换；长按 2s：下发 tune_mode=2（调谐中）。
 * - UI：tuneMode 为 1 或 2 时按钮为蓝色(#0066cc)，否则灰色；文案「关闭/开启/调谐中」。
 * - 路径影响：无；信号路径始终不经过天调，主管道4↔5 段固定走衰减器(attenuator8↔9)。
 */

const DEFAULT_ATTENUATOR_LIST = [0, 1, 3, 4];

/** 下一档 attenuator_sel 索引：+1 循环，含 0 档（0 dB） */
export function nextAttenuatorSelIndex(currentSel: number, list: number[]): number {
    if (list.length === 0) return 0;
    const current = ((currentSel % list.length) + list.length) % list.length;
    return (current + 1) % list.length;
}

function attenuatorDbText(sel: number, list: number[]): string {
    const db = list[sel];
    if (db === undefined || db === null) return '—';
    return `${db}DB`;
}

const TUNE_LONG_PRESS_MS = 2000;
const ANTENNA_LABEL_LONG_PRESS_MS = 600;


export const TopBarBottom: React.FC<TopBarBottomProps> = ({
    deviceId = '1f75d96b40e8ed8e',
    amplifierData,
    antennaMode = 'manual'
}) => {
    const { t } = useAmplifierI18n();
    const showGreenNodes = false;

    // 定义 AmplifierControlData 状态，用于存储需要下发的控制数据
    // 触发模式 triggerMode：0=PTT1(默认) 1=PP2 3=输入功率触发（与输入天线独立）
    const [amplifierControlData, setAmplifierControlData] = useState<AmplifierControlData>({
        frequency: 0,
        triggerMode: 0,
        tuneMode: 0,
        power: 0,
        inputAttenuator: 0,
        inputAntenna: 0,
        outputAntenna: 0,
        alarmWord: 0
    });

    // 确保deviceId存在
    useEffect(() => {
        console.log('TopBarBottom组件 - 接收到的设备ID:', deviceId);
        if (!deviceId) {
            console.warn('TopBarBottom组件 - 设备ID为空，可能导致部分功能无法正常工作');
        }
    }, [deviceId]);

    // 处理参数更新函数
    const handleParameterChange = async (newData: Partial<AmplifierControlData>) => {
        try {
            const updatedData = { ...amplifierControlData, ...newData };
            const isPaEnableRequest =
                newData.power === 1 && amplifierControlData.power === 0;
            // 协议 §5.7：开功放只是请求，设备确认并通过 remote_control 回显后才更新 UI。
            if (!isPaEnableRequest) {
                setAmplifierControlData(updatedData);
            }
            await amplifierMqttService.sendAmplifierControlDelta(deviceId, newData);
            console.log('TopBarBottom: 功放控制指令发送成功:', newData);
        } catch (error) {
            console.error('TopBarBottom: 功放控制指令发送失败:', error);
        }
    };

    // 当 amplifierData 变化时，更新 AmplifierControlData
    useEffect(() => {
        if (amplifierData) {
            // 更新 AmplifierControlData
            const controlData: AmplifierControlData = {
                frequency: amplifierData.frequency,
                triggerMode: amplifierData.triggerMode,
                tuneMode: amplifierData.tuneMode,
                power: amplifierData.power,
                inputAttenuator: amplifierData.inputAttenuator,
                inputAntenna: amplifierData.inputAntenna,
                outputAntenna: amplifierData.outputAntenna,
                alarmWord: amplifierData.alarmWord
            };
            setAmplifierControlData(controlData);
        }
    }, [amplifierData]);

    const [renameState, setRenameState] = useState<{
        key: AntennaLabelKey;
        index: number;
        draftName: string;
        draftFreqStartHz: string;
        draftFreqEndHz: string;
        defaultName: string;
    } | null>(null);
    const [isAntennaConfigSaving, setIsAntennaConfigSaving] = useState(false);

    const antennaConfig = amplifierData?.antennaConfig;

    const getAntennaLabelText = useCallback(
        (key: AntennaLabelKey, defaultText: string) => {
            const index = ANTENNA_LABEL_TO_INDEX[key];
            return resolveAntennaDisplayName(index, antennaConfig, defaultText);
        },
        [antennaConfig],
    );

    const tuneLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tuneLongPressFiredRef = useRef(false);
    const labelLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const labelLongPressFiredRef = useRef(false);

    const clearLabelLongPress = useCallback(() => {
        if (labelLongPressTimerRef.current) {
            clearTimeout(labelLongPressTimerRef.current);
            labelLongPressTimerRef.current = null;
        }
    }, []);

    useEffect(() => () => clearLabelLongPress(), [clearLabelLongPress]);

    const openAntennaRenameModal = useCallback(
        (key: AntennaLabelKey, defaultName: string) => {
            const index = ANTENNA_LABEL_TO_INDEX[key];
            const config = amplifierData?.antennaConfig ?? createDefaultAntennaConfig();
            setRenameState({
                key,
                index,
                draftName: config.names[index] ?? '',
                draftFreqStartHz: hzToInput(config.freqStartHz[index] ?? 0),
                draftFreqEndHz: hzToInput(config.freqEndHz[index] ?? 0),
                defaultName,
            });
        },
        [amplifierData?.antennaConfig],
    );

    const createAntennaLabelPointerHandlers = useCallback(
        (key: AntennaLabelKey, defaultName: string, onShortTap: () => void) => ({
            onPointerDown: (e: React.PointerEvent<SVGRectElement>) => {
                e.stopPropagation();
                e.currentTarget.setPointerCapture(e.pointerId);
                labelLongPressFiredRef.current = false;
                clearLabelLongPress();
                labelLongPressTimerRef.current = setTimeout(() => {
                    labelLongPressFiredRef.current = true;
                    openAntennaRenameModal(key, defaultName);
                }, ANTENNA_LABEL_LONG_PRESS_MS);
            },
            onPointerUp: (e: React.PointerEvent<SVGRectElement>) => {
                e.stopPropagation();
                const fired = labelLongPressFiredRef.current;
                clearLabelLongPress();
                if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                    e.currentTarget.releasePointerCapture(e.pointerId);
                }
                if (fired) {
                    labelLongPressFiredRef.current = false;
                    return;
                }
                onShortTap();
            },
            onPointerCancel: (e: React.PointerEvent<SVGRectElement>) => {
                e.stopPropagation();
                clearLabelLongPress();
                labelLongPressFiredRef.current = false;
            },
        }),
        [clearLabelLongPress, openAntennaRenameModal],
    );

    const renderAntennaLabel = useCallback(
        (options: {
            labelKey: AntennaLabelKey;
            x: number;
            y: number;
            className: string;
            defaultName: string;
            onShortTap: () => void;
            hitWidth?: number;
            hitHeight?: number;
        }) => {
            const {
                labelKey,
                x,
                y,
                className,
                defaultName,
                onShortTap,
                hitWidth = 76,
                hitHeight = 22,
            } = options;
            const pointerHandlers = createAntennaLabelPointerHandlers(
                labelKey,
                defaultName,
                onShortTap,
            );
            return (
                <>
                    <text
                        x={x}
                        y={y}
                        className={className}
                        textAnchor="middle"
                        dominantBaseline="middle"
                    >
                        {getAntennaLabelText(labelKey, defaultName)}
                    </text>
                    <rect
                        x={x - hitWidth / 2}
                        y={y - hitHeight / 2}
                        width={hitWidth}
                        height={hitHeight}
                        fill="transparent"
                        className="amp-antenna-label-hit"
                        style={{ touchAction: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                        {...pointerHandlers}
                    />
                </>
            );
        },
        [createAntennaLabelPointerHandlers, getAntennaLabelText],
    );

    const handleSaveAntennaConfig = useCallback(async () => {
        if (!renameState || isAntennaConfigSaving) return;

        const validation = validateAntennaFreqRangeHz(
            renameState.draftFreqStartHz,
            renameState.draftFreqEndHz,
        );
        if (!validation.ok || validation.freqStartHz === undefined || validation.freqEndHz === undefined) {
            message.error(
                validation.messageKey
                    ? t(validation.messageKey, validation.messageParams)
                    : t('频率范围无效'),
            );
            return;
        }

        const baseConfig = amplifierData?.antennaConfig ?? createDefaultAntennaConfig();
        const trimmedName = renameState.draftName.trim();
        const savedName =
            trimmedName && trimmedName !== renameState.defaultName ? trimmedName : '';
        const nextConfig = patchAntennaConfigAtIndex(baseConfig, renameState.index, {
            name: savedName,
            freqStartHz: validation.freqStartHz,
            freqEndHz: validation.freqEndHz,
        });

        setIsAntennaConfigSaving(true);
        try {
            await amplifierMqttService.sendAntennaConfigDelta(deviceId, nextConfig);
            setRenameState(null);
        } catch (error) {
            console.error('TopBarBottom: 天线配置下发失败:', error);
            message.error(t('天线配置保存失败'));
        } finally {
            setIsAntennaConfigSaving(false);
        }
    }, [amplifierData?.antennaConfig, deviceId, isAntennaConfigSaving, renameState]);

    const clearTuneLongPress = useCallback(() => {
        if (tuneLongPressTimerRef.current) {
            clearTimeout(tuneLongPressTimerRef.current);
            tuneLongPressTimerRef.current = null;
        }
    }, []);

    useEffect(() => () => clearTuneLongPress(), [clearTuneLongPress]);

    const handleTunePointerDown = (e: React.PointerEvent<SVGGElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        tuneLongPressFiredRef.current = false;
        clearTuneLongPress();
        tuneLongPressTimerRef.current = setTimeout(() => {
            tuneLongPressFiredRef.current = true;
            handleParameterChange({ tuneMode: 2 });
        }, TUNE_LONG_PRESS_MS);
    };

    const handleTunePointerUp = () => {
        clearTuneLongPress();
        if (tuneLongPressFiredRef.current) return;
        const current = amplifierData?.tuneMode ?? 0;
        const next = current === 1 ? 0 : 1;
        handleParameterChange({ tuneMode: next });
    };

    const handleTunePointerCancel = () => {
        clearTuneLongPress();
        tuneLongPressFiredRef.current = false;
    };

    /** 整体下移偏移（仅节点、按钮与文字，不移动整页） */
    const Y_OFFSET = 20;

    // 节点配置
    const pipePathConfig = useMemo(() => ({
        // 左侧输入天线
        start1: { x: 90, y: 57 + Y_OFFSET },
        start2: { x: 90, y: 167 + Y_OFFSET },
        // 衰减器
        attenuatorHead1: { x: 115, y: 15 + Y_OFFSET },
        attenuatorHead2: { x: 265, y: 15 + Y_OFFSET },
        attenuatorHead3: { x: 350, y: 15 + Y_OFFSET },
        attenuatorHead4: { x: 380, y: 15 + Y_OFFSET },
        attenuator1: { x: 115, y: 57 + Y_OFFSET },
        attenuator2: { x: 140, y: 57 + Y_OFFSET },
        attenuator3: { x: 240, y: 57 + Y_OFFSET },
        attenuator4: { x: 265, y: 57 + Y_OFFSET },
        attenuator5: { x: 290, y: 57 + Y_OFFSET },
        attenuator6: { x: 350, y: 57 + Y_OFFSET },
        attenuator7: { x: 620, y: 57 + Y_OFFSET },
        attenuator8: { x: 660, y: 57 + Y_OFFSET },
        attenuator9: { x: 820, y: 57 + Y_OFFSET },
        startRight1: { x: 290, y: 167 + Y_OFFSET },
        // 主管道节点
        mainPipe1: { x: 290, y: 120 + Y_OFFSET },
        mainPipe2: { x: 350, y: 120 + Y_OFFSET },
        mainPipe3: { x: 620, y: 120 + Y_OFFSET },
        mainPipe4: { x: 660, y: 120 + Y_OFFSET },
        mainPipe5: { x: 820, y: 120 + Y_OFFSET },
        mainPipe6: { x: 940, y: 120 + Y_OFFSET },
        //功放按钮附近节点9个
        ampRight1: { x: 350, y: 190 + Y_OFFSET },
        ampRight2: { x: 380, y: 190 + Y_OFFSET },
        ampRight3: { x: 480, y: 190 + Y_OFFSET },
        ampRight4: { x: 500, y: 190 + Y_OFFSET },
        ampRight5: { x: 600, y: 190 + Y_OFFSET },
        ampRight6: { x: 620, y: 190 + Y_OFFSET },
        ampRight7: { x: 660, y: 190 + Y_OFFSET },
        ampRight8: { x: 680, y: 190 + Y_OFFSET },
        ampRight9: { x: 780, y: 190 + Y_OFFSET },
        ampRight10: { x: 820, y: 190 + Y_OFFSET },
        // 右侧输出天线
        rightAntenna1: { x: 1060, y: 35 + Y_OFFSET },
        rightAntenna2: { x: 1060, y: 100 + Y_OFFSET },
        rightAntenna3: { x: 1060, y: 165 + Y_OFFSET },
        rightAntenna4: { x: 1060, y: 230 + Y_OFFSET },
        rightAntennaFinal1: { x: 940, y: 35 + Y_OFFSET },
        rightAntennaFinal2: { x: 940, y: 100 + Y_OFFSET },
        rightAntennaFinal3: { x: 940, y: 165 + Y_OFFSET },
        rightAntennaFinal4: { x: 940, y: 230 + Y_OFFSET }
    }), []);

    // 信号路径动画已迁移至 amplifierControl.css（amp-flow-left-to-right）

    // 生成接收模式信号路径（ptt_status=0：输出天线 → 输入天线）
    const generateReceivePath = useCallback(() => {
        if (!amplifierData || amplifierData.ptt !== 0 || !amplifierData.outputAntenna || !amplifierData.inputAntenna) {
            return '';
        }

        let path = '';

        // 1. 从输出天线到主管道6
        switch (amplifierData.outputAntenna) {
            case 1:
                path += `M ${pipePathConfig.rightAntenna1.x} ${pipePathConfig.rightAntenna1.y} L ${pipePathConfig.rightAntennaFinal1.x} ${pipePathConfig.rightAntennaFinal1.y} L ${pipePathConfig.mainPipe6.x} ${pipePathConfig.mainPipe6.y} `;
                break;
            case 2:
                path += `M ${pipePathConfig.rightAntenna2.x} ${pipePathConfig.rightAntenna2.y} L ${pipePathConfig.rightAntennaFinal2.x} ${pipePathConfig.rightAntennaFinal2.y} L ${pipePathConfig.mainPipe6.x} ${pipePathConfig.mainPipe6.y} `;
                break;
            case 3:
                path += `M ${pipePathConfig.rightAntenna3.x} ${pipePathConfig.rightAntenna3.y} L ${pipePathConfig.rightAntennaFinal3.x} ${pipePathConfig.rightAntennaFinal3.y} L ${pipePathConfig.mainPipe6.x} ${pipePathConfig.mainPipe6.y} `;
                break;
            case 4:
                path += `M ${pipePathConfig.rightAntenna4.x} ${pipePathConfig.rightAntenna4.y} L ${pipePathConfig.rightAntennaFinal4.x} ${pipePathConfig.rightAntennaFinal4.y} L ${pipePathConfig.mainPipe6.x} ${pipePathConfig.mainPipe6.y} `;
                break;
        }

        // 2. 主管道6到主管道5
        path += `L ${pipePathConfig.mainPipe5.x} ${pipePathConfig.mainPipe5.y} `;

        // 3. 主管道5到主管道4（路径不经过天调）
        path += `L ${pipePathConfig.attenuator9.x} ${pipePathConfig.attenuator9.y} L ${pipePathConfig.attenuator8.x} ${pipePathConfig.attenuator8.y} L ${pipePathConfig.mainPipe4.x} ${pipePathConfig.mainPipe4.y} `;

        // 4. 主管道4到主管道3
        path += `L ${pipePathConfig.mainPipe3.x} ${pipePathConfig.mainPipe3.y} `;

        // 5. 主管道3到主管道2

        path += `L ${pipePathConfig.attenuator7.x} ${pipePathConfig.attenuator7.y} L ${pipePathConfig.attenuator6.x} ${pipePathConfig.attenuator6.y} L ${pipePathConfig.mainPipe2.x} ${pipePathConfig.mainPipe2.y} `;


        // 6. 主管道2到主管道1
        path += `L ${pipePathConfig.mainPipe1.x} ${pipePathConfig.mainPipe1.y} `;

        // 7. 主管道1到输入天线（按 inputAntenna：1→天线1，2→天线2）
        if (amplifierData.inputAntenna === 1) {
            path += `L ${pipePathConfig.attenuator5.x} ${pipePathConfig.attenuator5.y} L ${pipePathConfig.attenuator4.x} ${pipePathConfig.attenuator4.y} L ${pipePathConfig.attenuatorHead2.x} ${pipePathConfig.attenuatorHead2.y} L ${pipePathConfig.attenuatorHead1.x} ${pipePathConfig.attenuatorHead1.y} L ${pipePathConfig.attenuator1.x} ${pipePathConfig.attenuator1.y} L ${pipePathConfig.start1.x} ${pipePathConfig.start1.y} `;
        } else {
            path += `L ${pipePathConfig.startRight1.x} ${pipePathConfig.startRight1.y} L ${pipePathConfig.start2.x} ${pipePathConfig.start2.y} `;
        }

        return path;
    }, [amplifierData, pipePathConfig]);

    // 生成发射模式信号路径（ptt_status=1：输入天线 → 输出天线）
    const generateTransmitPath = useCallback(() => {
        if (!amplifierData || amplifierData.ptt !== 1 || !amplifierData.outputAntenna || !amplifierData.inputAntenna) {
            return '';
        }

        let path = '';

        // 1. 从输入天线到主管道1
        if (amplifierData.inputAntenna === 1) {
            // 输入天线1：功放=1 穿过当前衰减器，功放=0 绕过当前衰减器
            if (amplifierData.power === 1) {
                path += `M ${pipePathConfig.start1.x} ${pipePathConfig.start1.y} L ${pipePathConfig.attenuator1.x} ${pipePathConfig.attenuator1.y} L ${pipePathConfig.attenuator2.x} ${pipePathConfig.attenuator2.y}`;
                path += ` L ${pipePathConfig.attenuator3.x} ${pipePathConfig.attenuator3.y} L ${pipePathConfig.attenuator4.x} ${pipePathConfig.attenuator4.y} L ${pipePathConfig.attenuator5.x} ${pipePathConfig.attenuator5.y} L ${pipePathConfig.mainPipe1.x} ${pipePathConfig.mainPipe1.y} `;
            } else {
                path += `M ${pipePathConfig.start1.x} ${pipePathConfig.start1.y} L ${pipePathConfig.attenuator1.x} ${pipePathConfig.attenuator1.y} L ${pipePathConfig.attenuatorHead1.x} ${pipePathConfig.attenuatorHead1.y} L ${pipePathConfig.attenuatorHead2.x} ${pipePathConfig.attenuatorHead2.y}
                L ${pipePathConfig.attenuator4.x} ${pipePathConfig.attenuator4.y} L ${pipePathConfig.attenuator5.x} ${pipePathConfig.attenuator5.y} L ${pipePathConfig.mainPipe1.x} ${pipePathConfig.mainPipe1.y} `;
            }
        } else {
            // 输入天线2
            path += `M ${pipePathConfig.start2.x} ${pipePathConfig.start2.y} L ${pipePathConfig.startRight1.x} ${pipePathConfig.startRight1.y} L ${pipePathConfig.mainPipe1.x} ${pipePathConfig.mainPipe1.y} `;
        }

        // 2. 主管道1到主管道2
        path += `L ${pipePathConfig.mainPipe2.x} ${pipePathConfig.mainPipe2.y} `;

        // 3. 主管道2到主管道3（根据功放状态）
        if (amplifierData.power === 1) {
            // 功放开启
            path += `L ${pipePathConfig.ampRight1.x} ${pipePathConfig.ampRight1.y} L ${pipePathConfig.ampRight2.x} ${pipePathConfig.ampRight2.y} L ${pipePathConfig.ampRight3.x} ${pipePathConfig.ampRight3.y} L ${pipePathConfig.ampRight4.x} ${pipePathConfig.ampRight4.y} L ${pipePathConfig.ampRight5.x} ${pipePathConfig.ampRight5.y} L ${pipePathConfig.ampRight6.x} ${pipePathConfig.ampRight6.y} L ${pipePathConfig.mainPipe3.x} ${pipePathConfig.mainPipe3.y} `;
        } else {
            // 功放关闭
            path += `L ${pipePathConfig.attenuator6.x} ${pipePathConfig.attenuator6.y} L ${pipePathConfig.attenuator7.x} ${pipePathConfig.attenuator7.y} L ${pipePathConfig.mainPipe3.x} ${pipePathConfig.mainPipe3.y} `;
        }

        // 4. 主管道3到主管道4
        path += `L ${pipePathConfig.mainPipe4.x} ${pipePathConfig.mainPipe4.y} `;

          // 5. 主管道4到主管道5（根据天调模式）
          if (amplifierData.tuneMode === 0) {
            // 天调关闭
            path += `L ${pipePathConfig.attenuator8.x} ${pipePathConfig.attenuator8.y} L ${pipePathConfig.attenuator9.x} ${pipePathConfig.attenuator9.y} L ${pipePathConfig.mainPipe5.x} ${pipePathConfig.mainPipe5.y} `;
        } else {
            // 天调开启或调谐中
            path += `L ${pipePathConfig.ampRight7.x} ${pipePathConfig.ampRight7.y} L ${pipePathConfig.ampRight8.x} ${pipePathConfig.ampRight8.y} L ${pipePathConfig.ampRight9.x} ${pipePathConfig.ampRight9.y} L ${pipePathConfig.ampRight10.x} ${pipePathConfig.ampRight10.y} L ${pipePathConfig.mainPipe5.x} ${pipePathConfig.mainPipe5.y} `;
        }

        // 6. 主管道5到主管道6
        path += `L ${pipePathConfig.mainPipe6.x} ${pipePathConfig.mainPipe6.y} `;

        // 7. 主管道6到输出天线
        switch (amplifierData.outputAntenna) {
            case 1:
                path += `L ${pipePathConfig.rightAntennaFinal1.x} ${pipePathConfig.rightAntennaFinal1.y} L ${pipePathConfig.rightAntenna1.x} ${pipePathConfig.rightAntenna1.y} `;
                break;
            case 2:
                path += `L ${pipePathConfig.rightAntennaFinal2.x} ${pipePathConfig.rightAntennaFinal2.y} L ${pipePathConfig.rightAntenna2.x} ${pipePathConfig.rightAntenna2.y} `;
                break;
            case 3:
                path += `L ${pipePathConfig.rightAntennaFinal3.x} ${pipePathConfig.rightAntennaFinal3.y} L ${pipePathConfig.rightAntenna3.x} ${pipePathConfig.rightAntenna3.y} `;
                break;
            case 4:
                path += `L ${pipePathConfig.rightAntennaFinal4.x} ${pipePathConfig.rightAntennaFinal4.y} L ${pipePathConfig.rightAntenna4.x} ${pipePathConfig.rightAntenna4.y} `;
                break;
        }

        return path;
    }, [amplifierData, pipePathConfig]);

    // 根据 ptt_status 生成信号路径：0 输出→输入，1 输入→输出
    const generateSignalPath = useMemo(() => {
        if (!amplifierData) {
            return '';
        }
        if (amplifierData.ptt === 0) {
            return generateReceivePath();
        }
        if (amplifierData.ptt === 1) {
            return generateTransmitPath();
        }
        return '';
    }, [amplifierData, generateReceivePath, generateTransmitPath]);

    return (
        <div className="amplifier-bottom">
            <svg className="amplifier-bottom__svg" width="100%" height={270 + Y_OFFSET} viewBox={`0 0 1250 ${270 + Y_OFFSET}`}>
                <defs>
                    <pattern id="amp-topology-grid" width="32" height="32" patternUnits="userSpaceOnUse">
                        <path
                            d="M 32 0 L 0 0 0 32"
                            fill="none"
                            stroke="oklch(1 0 0 / 0.045)"
                            strokeWidth="0.5"
                        />
                    </pattern>
                    <filter id="amp-signal-glow" x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="amp-signal-glow-strong" x="-40%" y="-40%" width="180%" height="180%">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="amp-node-glow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="blur" />
                        <feFlood floodColor="oklch(0.68 0.19 145)" floodOpacity="0.55" result="color" />
                        <feComposite in="color" in2="blur" operator="in" result="glow" />
                        <feMerge>
                            <feMergeNode in="glow" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="amp-node-glow-soft" x="-40%" y="-40%" width="180%" height="180%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" />
                        <feFlood floodColor="oklch(0.72 0.14 250)" floodOpacity="0.35" result="color" />
                        <feComposite in="color" in2="blur" operator="in" result="glow" />
                        <feMerge>
                            <feMergeNode in="glow" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="amp-node-glow-orange" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="3.5" result="blur" />
                        <feFlood floodColor="oklch(0.62 0.17 55)" floodOpacity="0.6" result="color" />
                        <feComposite in="color" in2="blur" operator="in" result="glow" />
                        <feMerge>
                            <feMergeNode in="glow" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="amp-node-glow-blue" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="blur" />
                        <feFlood floodColor="oklch(0.58 0.14 250)" floodOpacity="0.55" result="color" />
                        <feComposite in="color" in2="blur" operator="in" result="glow" />
                        <feMerge>
                            <feMergeNode in="glow" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="amp-node-glow-blue-strong" x="-60%" y="-60%" width="220%" height="220%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="5" result="blur" />
                        <feFlood floodColor="oklch(0.58 0.14 250)" floodOpacity="0.75" result="color" />
                        <feComposite in="color" in2="blur" operator="in" result="glow" />
                        <feMerge>
                            <feMergeNode in="glow" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                <rect
                    className="amp-topology-grid"
                    x="0"
                    y="0"
                    width="1250"
                    height={270 + Y_OFFSET}
                />

                {/* 信号路径 - 双层流光：外晕 + 内核 */}
                {amplifierData?.ptt === 0 && generateSignalPath && (
                    <>
                        <path d={generateSignalPath} className="amp-signal-path amp-signal-path--rx amp-signal-path--glow amp-signal-path--flow" />
                        <path d={generateSignalPath} className="amp-signal-path amp-signal-path--rx amp-signal-path--core amp-signal-path--flow" />
                    </>
                )}

                {amplifierData?.ptt === 1 && generateSignalPath && (
                    <>
                        <path d={generateSignalPath} className="amp-signal-path amp-signal-path--tx amp-signal-path--glow amp-signal-path--flow" />
                        <path d={generateSignalPath} className="amp-signal-path amp-signal-path--tx amp-signal-path--core amp-signal-path--flow" />
                    </>
                )}

                {showGreenNodes && (
                    <>
                        {/* 节点标记 - 绘制在按钮之后，确保可见 */}
                        {/* 左侧输入天线节点 */}
                        <circle cx={pipePathConfig.start1.x} cy={pipePathConfig.start1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.start2.x} cy={pipePathConfig.start2.y} r="4" fill="#00ff00" />
                        {/* 衰减器节点 */}
                        <circle cx={pipePathConfig.attenuatorHead1.x} cy={pipePathConfig.attenuatorHead1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuatorHead2.x} cy={pipePathConfig.attenuatorHead2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuatorHead3.x} cy={pipePathConfig.attenuatorHead3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuatorHead4.x} cy={pipePathConfig.attenuatorHead4.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator1.x} cy={pipePathConfig.attenuator1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator2.x} cy={pipePathConfig.attenuator2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator3.x} cy={pipePathConfig.attenuator3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator4.x} cy={pipePathConfig.attenuator4.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator5.x} cy={pipePathConfig.attenuator5.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator6.x} cy={pipePathConfig.attenuator6.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator7.x} cy={pipePathConfig.attenuator7.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator8.x} cy={pipePathConfig.attenuator8.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator9.x} cy={pipePathConfig.attenuator9.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.startRight1.x} cy={pipePathConfig.startRight1.y} r="4" fill="#00ff00" />

                        {/* 主管道节点 */}
                        <circle cx={pipePathConfig.mainPipe1.x} cy={pipePathConfig.mainPipe1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.mainPipe2.x} cy={pipePathConfig.mainPipe2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.mainPipe3.x} cy={pipePathConfig.mainPipe3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.mainPipe4.x} cy={pipePathConfig.mainPipe4.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.mainPipe5.x} cy={pipePathConfig.mainPipe5.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.mainPipe6.x} cy={pipePathConfig.mainPipe6.y} r="4" fill="#00ff00" />
                        {/* 功放按钮附近节点9个 */}
                        <circle cx={pipePathConfig.ampRight1.x} cy={pipePathConfig.ampRight1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight2.x} cy={pipePathConfig.ampRight2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight3.x} cy={pipePathConfig.ampRight3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight4.x} cy={pipePathConfig.ampRight4.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight5.x} cy={pipePathConfig.ampRight5.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight6.x} cy={pipePathConfig.ampRight6.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight7.x} cy={pipePathConfig.ampRight7.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight8.x} cy={pipePathConfig.ampRight8.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight9.x} cy={pipePathConfig.ampRight9.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight10.x} cy={pipePathConfig.ampRight10.y} r="4" fill="#00ff00" />
                        {/* 右侧输出天线节点 */}
                        <circle cx={pipePathConfig.rightAntenna1.x} cy={pipePathConfig.rightAntenna1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntenna2.x} cy={pipePathConfig.rightAntenna2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntenna3.x} cy={pipePathConfig.rightAntenna3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntenna4.x} cy={pipePathConfig.rightAntenna4.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntennaFinal1.x} cy={pipePathConfig.rightAntennaFinal1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntennaFinal2.x} cy={pipePathConfig.rightAntennaFinal2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntennaFinal3.x} cy={pipePathConfig.rightAntennaFinal3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntennaFinal4.x} cy={pipePathConfig.rightAntennaFinal4.y} r="4" fill="#00ff00" />
                    </>
                )}

                {/* 按钮绘制 - 绘制在信号路径之上，遮挡穿过的信号 */}
                <g className="amp-node-group amp-node-group--interactive" style={{ cursor: 'pointer' }} onClick={() => { /* 假负载点击可在此扩展 */ }}>
                    <rect x="380" y={Y_OFFSET - 5} width="100" height="40" fill={AMP_VIS.surfaceInset} rx={AMP_VIS.rx} ry={AMP_VIS.rx} stroke={AMP_VIS.border} strokeWidth="1" className="amp-node-rect" />
                    <text x="425" y={Y_OFFSET + 20} className="amp-node-label" textAnchor="middle" dominantBaseline="middle">{t('假负载')}</text>
                </g>

                <g className="amp-node-group amp-node-group--interactive" style={{ cursor: 'pointer' }} onClick={() => handleParameterChange({ inputAntenna: 1 })}>
                    <rect
                        x="0" y={40 + Y_OFFSET} width="90" height="35"
                        fill={ampNodeFill(amplifierData?.inputAntenna === 1)}
                        rx={AMP_VIS.rxSm} ry={AMP_VIS.rxSm}
                        stroke={ampNodeStroke(amplifierData?.inputAntenna === 1)}
                        strokeWidth="1"
                        className={ampNodeRectClass({ active: amplifierData?.inputAntenna === 1 })}
                    />
                    {renderAntennaLabel({
                        labelKey: 'input1',
                        x: 45,
                        y: 62 + Y_OFFSET,
                        className: 'amp-node-label',
                        defaultName: t('输入天线1'),
                        onShortTap: () => handleParameterChange({ inputAntenna: 1 }),
                    })}
                </g>

                <g className="amp-node-group">
                    <rect x="140" y={40 + Y_OFFSET} width="100" height="45" fill={AMP_VIS.surfaceInset} rx={AMP_VIS.rxSm} ry={AMP_VIS.rxSm} stroke={AMP_VIS.border} strokeWidth="1" className="amp-node-rect" />
                    <text x="190" y={55 + Y_OFFSET} className="amp-node-sublabel" textAnchor="middle">{t('输入天线衰减器')}</text>
                    <text x="190" y={70 + Y_OFFSET} className="amp-node-value" textAnchor="middle">6.0DB</text>
                </g>

                <g className="amp-node-group amp-node-group--interactive" style={{ cursor: 'pointer' }} onClick={() => handleParameterChange({ inputAntenna: 2 })}>
                    <rect
                        x="0" y={150 + Y_OFFSET} width="90" height="35"
                        fill={ampNodeFill(amplifierData?.inputAntenna === 2)}
                        rx={AMP_VIS.rxSm} ry={AMP_VIS.rxSm}
                        stroke={ampNodeStroke(amplifierData?.inputAntenna === 2)}
                        strokeWidth="1"
                        className={ampNodeRectClass({ active: amplifierData?.inputAntenna === 2 })}
                    />
                    {renderAntennaLabel({
                        labelKey: 'input2',
                        x: 45,
                        y: 172 + Y_OFFSET,
                        className: 'amp-node-label',
                        defaultName: t('输入天线2'),
                        onShortTap: () => handleParameterChange({ inputAntenna: 2 }),
                    })}
                </g>

                <g className="amp-node-group amp-node-group--interactive" style={{ cursor: 'pointer' }} onClick={() => {
                    const list = amplifierData?.attenuatorList ?? DEFAULT_ATTENUATOR_LIST;
                    const current = amplifierData?.inputAttenuator ?? amplifierControlData.inputAttenuator;
                    handleParameterChange({ inputAttenuator: nextAttenuatorSelIndex(current, list) });
                }}>
                    <rect
                        x="380" y={170 + Y_OFFSET} width="100" height="40"
                        fill={ampNodeFill(amplifierData?.power === 1, 'power')}
                        rx={AMP_VIS.rx} ry={AMP_VIS.rx}
                        stroke={ampNodeStroke(amplifierData?.power === 1, 'power')}
                        strokeWidth="1"
                        className={ampNodeRectClass({ active: amplifierData?.power === 1, power: amplifierData?.power === 1 })}
                    />
                    <text x="430" y={188 + Y_OFFSET} className="amp-node-label amp-node-label--sm" textAnchor="middle">{t('输入衰减器')}</text>
                    <text x="430" y={202 + Y_OFFSET} className="amp-node-sublabel" textAnchor="middle">
                        {attenuatorDbText(
                            Number(amplifierData?.inputAttenuator ?? amplifierControlData.inputAttenuator),
                            amplifierData?.attenuatorList ?? DEFAULT_ATTENUATOR_LIST,
                        )}
                    </text>
                </g>

                <g className="amp-node-group amp-node-group--interactive" style={{ cursor: 'pointer' }} onClick={() => {
                    console.log('TopBarBottom - 点击功放按钮');
                    console.info('TopBarBottom - 当前power值:', amplifierControlData.power);
                    const newPower = amplifierControlData.power === 0 ? 1 : 0;
                    handleParameterChange({ power: newPower });
                }}>
                    <rect
                        x="500" y={170 + Y_OFFSET} width="100" height="40"
                        fill={ampNodeFill(amplifierData?.power === 1, 'power')}
                        rx={AMP_VIS.rx} ry={AMP_VIS.rx}
                        stroke={ampNodeStroke(amplifierData?.power === 1, 'power')}
                        strokeWidth="1"
                        className={ampNodeRectClass({ active: amplifierData?.power === 1, power: amplifierData?.power === 1 })}
                    />
                    <text x="550" y={190 + Y_OFFSET} className="amp-node-label" textAnchor="middle" dominantBaseline="middle">{t('功放')}</text>
                </g>

                <g
                    className={`amp-node-group amp-node-group--interactive${amplifierData?.tuneMode === 2 ? ' amp-node-group--tuning' : ''}`}
                    style={{ cursor: 'pointer', touchAction: 'none' }}
                    onPointerDown={handleTunePointerDown}
                    onPointerUp={handleTunePointerUp}
                    onPointerCancel={handleTunePointerCancel}
                    onPointerLeave={handleTunePointerCancel}
                >
                    <rect
                        x="680" y={170 + Y_OFFSET} width="100" height="40"
                        fill={ampNodeFill(amplifierData?.tuneMode === 1 || amplifierData?.tuneMode === 2, 'tune')}
                        rx={AMP_VIS.rx} ry={AMP_VIS.rx}
                        stroke={ampNodeStroke(amplifierData?.tuneMode === 1 || amplifierData?.tuneMode === 2, 'tune')}
                        strokeWidth="1"
                        className={ampNodeRectClass({
                            active: amplifierData?.tuneMode === 1 || amplifierData?.tuneMode === 2,
                            tune: amplifierData?.tuneMode === 1 || amplifierData?.tuneMode === 2,
                            tuning: amplifierData?.tuneMode === 2,
                        })}
                    />
                    <text x="730" y={188 + Y_OFFSET} className="amp-node-label amp-node-label--sm" textAnchor="middle">{t('天调')}</text>
                    <text x="730" y={202 + Y_OFFSET} className="amp-node-sublabel" textAnchor="middle">
                        {(() => {
                            switch (amplifierData?.tuneMode) {
                                case 0:
                                    return t('关闭');
                                case 1:
                                    return t('开启');
                                case 2:
                                    return t('调谐中');
                                default:
                                    return t('关闭');
                            }
                        })()}
                    </text>
                </g>

                <g
                    className={`amp-node-group amp-node-group--interactive${antennaMode !== 'manual' ? ' amp-node-group--disabled' : ''}`}
                    style={{ cursor: antennaMode === 'manual' ? 'pointer' : 'not-allowed' }}
                    onClick={() => {
                    if (antennaMode === 'manual') {
                        console.log('TopBarBottom - 点击输出天线1按钮');
                        console.log('TopBarBottom - 修改临时状态并下发指令');
                        handleParameterChange({ outputAntenna: 1 });
                    }
                }}>
                    <line x1="1060" y1={35 + Y_OFFSET} x2="940" y2={35 + Y_OFFSET} className="amp-connector-line" />
                    <rect
                        x="1060" y={20 + Y_OFFSET} width="80" height="30"
                        fill={ampNodeFill(amplifierData?.outputAntenna === 1)}
                        rx={AMP_VIS.rxSm} ry={AMP_VIS.rxSm}
                        stroke={ampNodeStroke(amplifierData?.outputAntenna === 1)}
                        strokeWidth="1"
                        className={ampNodeRectClass({ active: amplifierData?.outputAntenna === 1 })}
                    />
                    {renderAntennaLabel({
                        labelKey: 'output1',
                        x: 1100,
                        y: 40 + Y_OFFSET,
                        className: 'amp-node-label amp-node-label--sm',
                        defaultName: t('输出天线1'),
                        hitWidth: 72,
                        hitHeight: 20,
                        onShortTap: () => {
                            if (antennaMode === 'manual') {
                                handleParameterChange({ outputAntenna: 1 });
                            }
                        },
                    })}
                </g>

                <g
                    className={`amp-node-group amp-node-group--interactive${antennaMode !== 'manual' ? ' amp-node-group--disabled' : ''}`}
                    style={{ cursor: antennaMode === 'manual' ? 'pointer' : 'not-allowed' }}
                    onClick={() => {
                    if (antennaMode === 'manual') {
                        console.log('TopBarBottom - 点击输出天线2按钮');
                        console.log('TopBarBottom - 修改临时状态并下发指令');
                        handleParameterChange({ outputAntenna: 2 });
                    }
                }}>
                    <line x1="1060" y1={100 + Y_OFFSET} x2="1000" y2={100 + Y_OFFSET} className="amp-connector-line" />
                    <rect
                        x="1060" y={85 + Y_OFFSET} width="80" height="30"
                        fill={ampNodeFill(amplifierData?.outputAntenna === 2)}
                        rx={AMP_VIS.rxSm} ry={AMP_VIS.rxSm}
                        stroke={ampNodeStroke(amplifierData?.outputAntenna === 2)}
                        strokeWidth="1"
                        className={ampNodeRectClass({ active: amplifierData?.outputAntenna === 2 })}
                    />
                    {renderAntennaLabel({
                        labelKey: 'output2',
                        x: 1100,
                        y: 105 + Y_OFFSET,
                        className: 'amp-node-label amp-node-label--sm',
                        defaultName: t('输出天线2'),
                        hitWidth: 72,
                        hitHeight: 20,
                        onShortTap: () => {
                            if (antennaMode === 'manual') {
                                handleParameterChange({ outputAntenna: 2 });
                            }
                        },
                    })}
                </g>

                <g
                    className={`amp-node-group amp-node-group--interactive${antennaMode !== 'manual' ? ' amp-node-group--disabled' : ''}`}
                    style={{ cursor: antennaMode === 'manual' ? 'pointer' : 'not-allowed' }}
                    onClick={() => {
                    if (antennaMode === 'manual') {
                        console.log('TopBarBottom - 点击输出天线3按钮');
                        console.log('TopBarBottom - 修改临时状态并下发指令');
                        handleParameterChange({ outputAntenna: 3 });
                    }
                }}>
                    <line x1="1060" y1={165 + Y_OFFSET} x2="1000" y2={165 + Y_OFFSET} className="amp-connector-line" />
                    <rect
                        x="1060" y={150 + Y_OFFSET} width="80" height="30"
                        fill={ampNodeFill(amplifierData?.outputAntenna === 3)}
                        rx={AMP_VIS.rxSm} ry={AMP_VIS.rxSm}
                        stroke={ampNodeStroke(amplifierData?.outputAntenna === 3)}
                        strokeWidth="1"
                        className={ampNodeRectClass({ active: amplifierData?.outputAntenna === 3 })}
                    />
                    {renderAntennaLabel({
                        labelKey: 'output3',
                        x: 1100,
                        y: 170 + Y_OFFSET,
                        className: 'amp-node-label amp-node-label--sm',
                        defaultName: t('输出天线3'),
                        hitWidth: 72,
                        hitHeight: 20,
                        onShortTap: () => {
                            if (antennaMode === 'manual') {
                                handleParameterChange({ outputAntenna: 3 });
                            }
                        },
                    })}
                </g>

                <g
                    className={`amp-node-group amp-node-group--interactive${antennaMode !== 'manual' ? ' amp-node-group--disabled' : ''}`}
                    style={{ cursor: antennaMode === 'manual' ? 'pointer' : 'not-allowed' }}
                    onClick={() => {
                    if (antennaMode === 'manual') {
                        console.log('TopBarBottom - 点击输出天线4按钮');
                        console.log('TopBarBottom - 修改临时状态并下发指令');
                        handleParameterChange({ outputAntenna: 4 });
                    }
                }}>
                    <line x1="1060" y1={230 + Y_OFFSET} x2="940" y2={230 + Y_OFFSET} className="amp-connector-line" />
                    <rect
                        x="1060" y={215 + Y_OFFSET} width="80" height="30"
                        fill={ampNodeFill(amplifierData?.outputAntenna === 4)}
                        rx={AMP_VIS.rxSm} ry={AMP_VIS.rxSm}
                        stroke={ampNodeStroke(amplifierData?.outputAntenna === 4)}
                        strokeWidth="1"
                        className={ampNodeRectClass({ active: amplifierData?.outputAntenna === 4 })}
                    />
                    {renderAntennaLabel({
                        labelKey: 'output4',
                        x: 1100,
                        y: 235 + Y_OFFSET,
                        className: 'amp-node-label amp-node-label--sm',
                        defaultName: t('输出天线4'),
                        hitWidth: 72,
                        hitHeight: 20,
                        onShortTap: () => {
                            if (antennaMode === 'manual') {
                                handleParameterChange({ outputAntenna: 4 });
                            }
                        },
                    })}
                </g>

            </svg>
            <AntennaNameEditModal
                open={renameState !== null}
                defaultName={renameState?.defaultName ?? ''}
                nameValue={renameState?.draftName ?? ''}
                freqStartHz={renameState?.draftFreqStartHz ?? ''}
                freqEndHz={renameState?.draftFreqEndHz ?? ''}
                saving={isAntennaConfigSaving}
                onNameChange={(draftName) => {
                    setRenameState((prev) => (prev ? { ...prev, draftName } : null));
                }}
                onFreqStartChange={(draftFreqStartHz) => {
                    setRenameState((prev) => (prev ? { ...prev, draftFreqStartHz } : null));
                }}
                onFreqEndChange={(draftFreqEndHz) => {
                    setRenameState((prev) => (prev ? { ...prev, draftFreqEndHz } : null));
                }}
                onSave={() => void handleSaveAntennaConfig()}
                onCancel={() => {
                    if (!isAntennaConfigSaving) setRenameState(null);
                }}
            />
        </div>
    );
};

export default TopBarBottom;